import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import DateRangerPicker, { ISelectDate } from 'components/DateRangerPicker'
import { usePopup } from 'hooks/usePopup'
import { IOnChange } from 'interfaces/IPagination'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IRouteLimit, IRouteOverflowAction, routeService } from 'services/routeService'
import TableCustom from 'shared/TableCustom'
import NoData from 'shared/TableCustom/NoData'
import tableConfig from 'shared/TableCustom/TableConfig'
import { IColumn } from 'shared/TableCustom/types'

import {
  describeRouteOverflowCause,
  describeRouteOverflowResolution,
  formatRouteOverflowDate,
  routeOverflowCauseSummary,
  routeOverflowSourceLabel,
} from '../routeOverflowPresentation'
import {
  ApplyButton,
  BackButton,
  CauseSummary,
  ClearButton,
  Counter,
  Details,
  FilterActions,
  FilterField,
  FilterLabel,
  Filters,
  LegacyNotice,
  MobileCard,
  MobileCardContent,
  MobileCardHeader,
  MobileCardMeta,
  MobileCardOccurrence,
  MobileExpandButton,
  MobileHistory,
  MobileLoading,
  MobileMetaLabel,
  MobilePageSize,
  MobilePagination,
  OverflowBadge,
  Page,
  PageHeader,
  StatusChip,
  TableWrapper,
  Timeline,
  TimelineDot,
  TimelineEmpty,
  TimelineItem,
} from './styles'

type HistoryFilters = {
  route: string
  status: string
  from: string
  to: string
}

type HistoryRequestError = {
  message?: string
  msg?: string
}

const EMPTY_FILTERS: HistoryFilters = { route: '', status: '', from: '', to: '' }

const actionLabels: Record<string, string> = {
  detected: 'Estouro identificado',
  resolved: 'Rota regularizada',
  order_added_to_route: 'Pedido incluído na rota',
  order_moved_to_route: 'Pedido transferido para a rota',
  order_removed_from_route: 'Pedido retirado da rota',
  multiple_order_box_counts: 'Vários pedidos atualizados',
  order_reduction: 'Quantidade do pedido reduzida',
  order_box_count_changed: 'Quantidade de caixas atualizada',
  order_removed: 'Pedido removido da rota',
  order_moved: 'Pedido transferido',
  vehicle_capacity_changed: 'Capacidade do veículo alterada',
  vehicle_changed: 'Veículo alterado',
  initial_route_composition: 'Composição inicial da rota',
  automatic_reconciliation: 'Regularização automática',
  unknown: 'Ação não identificada',
}

const formatDate = formatRouteOverflowDate

const parseFilterDate = (value: string) => {
  if (!value) return undefined
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const formatFilterDate = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getStatus = (item: IRouteLimit) => {
  if (!item.detected_at) {
    return { label: 'Registro legado', color: '#475467', background: '#F2F4F7' }
  }
  if (item.status === 'pending') {
    return { label: 'Pendente', color: '#8A4B00', background: '#FFF4E5' }
  }
  if (item.status === 'allowed_over_capacity') {
    return { label: 'Autorização antiga', color: '#475467', background: '#F2F4F7' }
  }
  return { label: 'Resolvida', color: '#315B0B', background: '#EAF5E1' }
}

const getRoute = (item: IRouteLimit) => item.route_number ?? item.route

const getCapacity = (item: IRouteLimit) => ({
  total: item.detected_total ?? item.current_total,
  capacity: item.detected_capacity ?? item.current_capacity,
})

const getResolutionPresentation = describeRouteOverflowResolution

const formatActionCounts = (action: IRouteOverflowAction) => {
  const previous = action.previous_box_count
  const current = action.current_box_count
  if (current === null || current === undefined) return null
  const order = action.order_id ? `Pedido ${action.order_id}: ` : ''
  if (previous === null || previous === undefined) return `${order}${current} caixas registradas`
  if (previous === current) return `${order}${current} caixas confirmadas`
  return `${order}${previous} → ${current} caixas`
}

const formatRouteState = (total?: number | null, capacity?: number | null) => {
  if (total === null || total === undefined) return null
  if (capacity === null || capacity === undefined) return `${total} caixas na rota`
  const overflow = Math.max(0, total - capacity)
  return `${total} caixas na rota · capacidade ${capacity}${
    overflow > 0 ? ` · excesso de ${overflow} ${overflow === 1 ? 'caixa' : 'caixas'}` : ''
  }`
}

const actionDetails = (action: IRouteOverflowAction) => {
  const count = formatActionCounts(action)
  const changes = Array.isArray(action.details?.changes) ? action.details.changes : []

  if (action.action_type === 'detected') {
    const routeState = formatRouteState(action.current_total, action.current_capacity)
    if (
      action.previous_total !== null &&
      action.previous_total !== undefined &&
      action.current_total !== null &&
      action.current_total !== undefined &&
      action.previous_total !== action.current_total
    ) {
      return [
        `Antes: ${action.previous_total} caixas.`,
        routeState ? `No estouro: ${routeState}.` : null,
      ].filter(Boolean) as string[]
    }
    return routeState ? [`No estouro: ${routeState}.`] : []
  }

  if (action.action_type === 'resolved') {
    const routeState = formatRouteState(action.current_total, action.current_capacity)
    return [routeState ? `Estado final da rota: ${routeState}.` : null, count].filter(
      Boolean,
    ) as string[]
  }

  if (count) return [count]
  if (changes.length) return [`${changes.length} pedidos atualizados`]
  if (
    action.previous_capacity !== null &&
    action.previous_capacity !== undefined &&
    action.current_capacity !== null &&
    action.current_capacity !== undefined &&
    action.previous_capacity !== action.current_capacity
  ) {
    return [`Capacidade da rota: ${action.previous_capacity} → ${action.current_capacity} caixas`]
  }
  const routeState = formatRouteState(action.current_total, action.current_capacity)
  return routeState ? [routeState] : []
}

const getTimelineActions = (item: IRouteLimit) => {
  const detectedTotal = item.detected_total ?? item.current_total
  const detectedCapacity = item.detected_capacity ?? item.current_capacity
  let causeActionRemoved = false

  return (item.actions || []).filter(action => {
    if (action.action_type === 'detected') return false
    if (action.action_type === 'initial_route_composition') return false

    const isCauseAction =
      !causeActionRemoved &&
      action.action_type === item.cause_type &&
      action.current_total === detectedTotal &&
      action.current_capacity === detectedCapacity &&
      (!item.trigger_order_id || action.order_id === item.trigger_order_id) &&
      (!item.trigger_source || action.source === item.trigger_source)

    if (isCauseAction) {
      causeActionRemoved = true
      return false
    }

    return true
  })
}

const RoutesOverflowHistory = () => {
  const navigate = useNavigate()
  const { addPopup } = usePopup()
  const controller = useRef<AbortController | null>(null)
  const [results, setResults] = useState<IRouteLimit[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [loading, setLoading] = useState(false)
  const [tableStatus, setTableStatus] = useState<'initial' | 'pending' | 'success' | 'error'>(
    'initial',
  )
  const [expanded, setExpanded] = useState<number | null>(null)
  const [filters, setFilters] = useState<HistoryFilters>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<HistoryFilters>(EMPTY_FILTERS)

  const loadHistory = async (
    pageNumber: number,
    selectedFilters = appliedFilters,
    selectedPageSize = pageSize,
  ) => {
    controller.current?.abort()
    const requestController = new AbortController()
    controller.current = requestController
    try {
      setLoading(true)
      setTableStatus('pending')
      const data = await routeService.getRouteOverflowHistory({
        page: pageNumber,
        pageSize: selectedPageSize,
        route: selectedFilters.route ? Number(selectedFilters.route) : undefined,
        status: selectedFilters.status || undefined,
        from: selectedFilters.from || undefined,
        to: selectedFilters.to || undefined,
        controller: requestController,
      })
      setResults(data.results)
      setTotalCount(data.totalCount)
      setPage(pageNumber)
      setExpanded(null)
      setTableStatus('success')
    } catch (error) {
      if (requestController.signal.aborted) return
      setTableStatus('error')
      const err = error as HistoryRequestError
      addPopup({
        type: 'error',
        title: 'Erro ao carregar o histórico',
        description: err?.message ?? err?.msg ?? 'Erro desconhecido',
        autoClose: false,
      })
    } finally {
      if (!requestController.signal.aborted) setLoading(false)
    }
  }

  useEffect(() => {
    loadHistory(1, EMPTY_FILTERS)
    return () => controller.current?.abort()
  }, [])

  const applyFilters = (event: FormEvent) => {
    event.preventDefault()
    if (filters.from && filters.to && filters.from > filters.to) {
      addPopup({
        type: 'error',
        title: 'Período inválido',
        description: 'A data inicial deve ser anterior à data final.',
      })
      return
    }
    setAppliedFilters(filters)
    loadHistory(1, filters)
  }

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    loadHistory(1, EMPTY_FILTERS)
  }

  const selectPeriod = ({ startDate, endDate }: ISelectDate) => {
    setFilters(current => ({
      ...current,
      from: formatFilterDate(startDate),
      to: formatFilterDate(endDate),
    }))
  }

  const changeTable = (changes: IOnChange) => {
    const nextPage = typeof changes.page === 'number' ? changes.page : page
    const nextPageSize = typeof changes.pageSize === 'number' ? changes.pageSize : pageSize
    setPageSize(nextPageSize)
    loadHistory(nextPage, appliedFilters, nextPageSize)
  }

  const columns: IColumn<IRouteLimit>[] = [
    {
      field: 'route',
      label: 'Rota',
      disabledSort: true,
      style: { width: '16%' },
      renderCell: ({ data: item }) => {
        const route = getRoute(item)
        return (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 700 }}>
              {route !== null && route !== undefined ? `Rota ${route}` : 'Rota indisponível'}
            </Typography>
            {item.license_plate && (
              <Typography variant="caption" color="textSecondary">
                Veículo {item.license_plate}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'detected_at',
      label: 'Ocorrência',
      disabledSort: true,
      style: { width: '27%' },
      renderCell: ({ data: item }) => {
        const isLegacy = !item.detected_at
        const cause = routeOverflowCauseSummary(item.cause_type) || item.cause_label
        return (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {isLegacy ? 'Auditoria indisponível' : cause || 'Motivo não identificado'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(item.detected_at) || 'Registro anterior à auditoria detalhada'}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'current_total',
      label: 'Capacidade',
      disabledSort: true,
      style: { width: '16%' },
      renderCell: ({ data: item }) => {
        const capacity = getCapacity(item)
        const total = capacity.total
        const limit = capacity.capacity
        const hasCapacity =
          total !== null && total !== undefined && limit !== null && limit !== undefined
        const overflow = hasCapacity ? Math.max(0, Number(total) - Number(limit)) : null

        return hasCapacity ? (
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {total} de {limit}
            </Typography>
            {overflow !== null && overflow > 0 && <OverflowBadge>+{overflow}</OverflowBadge>}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        )
      },
    },
    {
      field: 'status',
      label: 'Status',
      disabledSort: true,
      style: { width: '15%' },
      renderCell: ({ data: item }) => {
        const status = getStatus(item)
        return (
          <StatusChip
            size="small"
            label={status.label}
            style={{ color: status.color, background: status.background }}
          />
        )
      },
    },
    {
      field: 'resolved_at',
      label: 'Resolução',
      disabledSort: true,
      style: { width: '22%' },
      renderCell: ({ data: item }) => {
        const resolution = getResolutionPresentation(item)
        return (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {resolution.title}
            </Typography>
            {resolution.detail && (
              <Typography variant="caption" color="textSecondary" display="block">
                {resolution.detail}
              </Typography>
            )}
            {resolution.metadata && (
              <Typography variant="caption" color="textSecondary" display="block">
                {resolution.metadata}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'actions',
      label: '',
      align: 'right',
      disabledSort: true,
      style: { width: '4%' },
      renderCell: ({ data: item }) => {
        const route = getRoute(item)
        const isExpanded = expanded === item.id
        return (
          <IconButton
            size="small"
            aria-label={`${isExpanded ? 'Ocultar' : 'Exibir'} ações da ${
              route !== null && route !== undefined ? `rota ${route}` : 'rota'
            }`}
            aria-expanded={isExpanded}
            onClick={() => setExpanded(isExpanded ? null : item.id)}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )
      },
    },
  ]

  const renderDetailPanel = (item: IRouteLimit) => {
    const isLegacy = !item.detected_at
    const timelineActions = getTimelineActions(item)
    return (
      <Details>
        {isLegacy ? (
          <Box>
            <LegacyNotice>
              Registro legado — os detalhes de auditoria não estavam disponíveis quando esta
              ocorrência foi criada.
            </LegacyNotice>
          </Box>
        ) : (
          <>
            <CauseSummary>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                O que fez a rota exceder a capacidade
              </Typography>
              <Typography variant="body2" style={{ marginTop: '.25rem' }}>
                {describeRouteOverflowCause(item)}
              </Typography>
            </CauseSummary>

            <Box mt="1.25rem">
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                O que aconteceu depois
              </Typography>
              {timelineActions.length === 0 ? (
                <TimelineEmpty>Nenhuma ação registrada após o estouro.</TimelineEmpty>
              ) : (
                <Timeline>
                  {timelineActions.map(action => {
                    const details = actionDetails(action)
                    return (
                      <TimelineItem key={action.id}>
                        <TimelineDot />
                        <Box>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {actionLabels[action.action_type] || action.action_type}
                          </Typography>
                          {details.map(detail => (
                            <Typography key={detail} variant="body2" color="textSecondary">
                              {detail}
                            </Typography>
                          ))}
                          {action.source && (
                            <Typography variant="body2" color="textSecondary">
                              Origem: {routeOverflowSourceLabel(action.source)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(action.occurred_at)}
                            {action.actor ? ` · ${action.actor}` : ''}
                          </Typography>
                        </Box>
                      </TimelineItem>
                    )
                  })}
                </Timeline>
              )}
            </Box>
          </>
        )}
      </Details>
    )
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const renderMobileCard = (item: IRouteLimit) => {
    const route = getRoute(item)
    const status = getStatus(item)
    const capacity = getCapacity(item)
    const total = capacity.total
    const limit = capacity.capacity
    const hasCapacity =
      total !== null && total !== undefined && limit !== null && limit !== undefined
    const overflow = hasCapacity ? Math.max(0, Number(total) - Number(limit)) : null
    const isLegacy = !item.detected_at
    const cause = routeOverflowCauseSummary(item.cause_type) || item.cause_label
    const resolution = getResolutionPresentation(item)
    const isExpanded = expanded === item.id

    return (
      <MobileCard key={item.id}>
        <MobileCardContent>
          <MobileCardHeader>
            <Box minWidth={0}>
              <Typography variant="body2" style={{ fontWeight: 700 }}>
                {route !== null && route !== undefined ? `Rota ${route}` : 'Rota indisponível'}
              </Typography>
              {item.license_plate && (
                <Typography variant="caption" color="textSecondary">
                  Veículo {item.license_plate}
                </Typography>
              )}
            </Box>
            <StatusChip
              size="small"
              label={status.label}
              style={{ color: status.color, background: status.background }}
            />
          </MobileCardHeader>

          <MobileCardOccurrence>
            <MobileMetaLabel>Ocorrência</MobileMetaLabel>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {isLegacy ? 'Auditoria indisponível' : cause || 'Motivo não identificado'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(item.detected_at) || 'Registro anterior à auditoria detalhada'}
            </Typography>
          </MobileCardOccurrence>

          <MobileCardMeta>
            <Box minWidth={0}>
              <MobileMetaLabel>Capacidade no estouro</MobileMetaLabel>
              {hasCapacity ? (
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" style={{ fontWeight: 700 }}>
                    {total} de {limit}
                  </Typography>
                  {overflow !== null && overflow > 0 && <OverflowBadge>+{overflow}</OverflowBadge>}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Não informada
                </Typography>
              )}
            </Box>
            <Box minWidth={0}>
              <MobileMetaLabel>Resolução</MobileMetaLabel>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {resolution.title}
              </Typography>
              {resolution.detail && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {resolution.detail}
                </Typography>
              )}
              {resolution.metadata && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {resolution.metadata}
                </Typography>
              )}
            </Box>
          </MobileCardMeta>
        </MobileCardContent>

        <MobileExpandButton
          aria-label={`${isExpanded ? 'Ocultar' : 'Exibir'} detalhes da ${
            route !== null && route !== undefined ? `rota ${route}` : 'rota'
          }`}
          aria-expanded={isExpanded}
          onClick={() => setExpanded(isExpanded ? null : item.id)}
        >
          {isExpanded ? 'Ocultar detalhes' : 'Ver causa e histórico'}
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </MobileExpandButton>

        {isExpanded && renderDetailPanel(item)}
      </MobileCard>
    )
  }

  return (
    <Page>
      <PageHeader>
        <Box>
          <BackButton
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate('/operacao/conferencia/inicio?tab=routes-over&skipRecovery=true')
            }
          >
            Voltar para rotas estouradas
          </BackButton>
          <Typography variant="h4" style={{ fontSize: '1.5rem', fontWeight: 600 }}>
            Histórico de rotas estouradas
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: '.25rem' }}>
            Consulte o que causou cada estouro e como a rota foi regularizada.
          </Typography>
        </Box>
        <Counter>{totalCount} ocorrências</Counter>
      </PageHeader>

      <Filters component="form" onSubmit={applyFilters}>
        <FilterField>
          <FilterLabel htmlFor="route-history-search">Buscar rota</FilterLabel>
          <TextField
            id="route-history-search"
            value={filters.route}
            onChange={event =>
              setFilters(current => ({
                ...current,
                route: event.target.value.replace(/\D/g, ''),
              }))
            }
            placeholder="Digite o número da rota"
            size="small"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#7B8794', fontSize: '1.2rem' }} />
                </InputAdornment>
              ),
            }}
          />
        </FilterField>

        <FilterField>
          <FilterLabel id="route-history-status-label">Status</FilterLabel>
          <Select
            aria-labelledby="route-history-status-label"
            value={filters.status}
            onChange={event =>
              setFilters(current => ({ ...current, status: String(event.target.value) }))
            }
            displayEmpty
            variant="outlined"
          >
            <MenuItem value="">Todos os status</MenuItem>
            <MenuItem value="pending">Pendentes</MenuItem>
            <MenuItem value="resolved">Resolvidas</MenuItem>
            <MenuItem value="allowed_over_capacity">Autorizações antigas</MenuItem>
          </Select>
        </FilterField>

        <FilterField>
          <DateRangerPicker
            allowEmpty
            actionLabel="Selecionar"
            title="Período"
            startDate={parseFilterDate(filters.from)}
            endDate={parseFilterDate(filters.to)}
            onSelectedDate={selectPeriod}
            classContainer="history-date-range"
          />
        </FilterField>

        <FilterActions>
          <ClearButton type="button" disabled={loading} onClick={clearFilters}>
            Limpar
          </ClearButton>
          <ApplyButton type="submit" variant="contained" disabled={loading}>
            Aplicar filtros
          </ApplyButton>
        </FilterActions>
      </Filters>

      <TableWrapper>
        <TableCustom
          keyId="id"
          columns={columns}
          data={results}
          params={{ page, pageSize }}
          onChange={changeTable}
          refresh={() => loadHistory(page)}
          count={totalCount}
          status={tableStatus}
          hover
          isDetailPanelOpen={item => expanded === item.id}
          renderDetailPanel={({ data: item }) => renderDetailPanel(item)}
        />
      </TableWrapper>

      <MobileHistory>
        {tableStatus === 'pending' || tableStatus === 'initial' ? (
          <MobileCard>
            <MobileLoading>
              <CircularProgress size={28} />
            </MobileLoading>
          </MobileCard>
        ) : results.length === 0 ? (
          <MobileCard>
            <NoData
              status={tableStatus}
              refresh={() => loadHistory(page)}
              style={{ height: '15rem' }}
            />
          </MobileCard>
        ) : (
          <>
            {results.map(renderMobileCard)}
            <MobilePagination>
              <Box display="flex" alignItems="center">
                <IconButton
                  size="small"
                  aria-label="Página anterior"
                  disabled={loading || page <= 1}
                  onClick={() => changeTable({ page: page - 1 })}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="body2" style={{ minWidth: '5rem', textAlign: 'center' }}>
                  {page} de {totalPages}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Próxima página"
                  disabled={loading || page >= totalPages}
                  onClick={() => changeTable({ page: page + 1 })}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <MobilePageSize
                value={pageSize}
                inputProps={{ 'aria-label': 'Ocorrências por página' }}
                onChange={event => changeTable({ page: 1, pageSize: Number(event.target.value) })}
              >
                {tableConfig.rowsPerPageOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option} / página
                  </MenuItem>
                ))}
              </MobilePageSize>
            </MobilePagination>
          </>
        )}
      </MobileHistory>
    </Page>
  )
}

export default RoutesOverflowHistory
